import { BaseProvider } from "./base.provider";

export type ReportCategory =
  | "harassment"
  | "explicit_content"
  | "dangerous_behavior"
  | "illegal_activity"
  | "hate_speech"
  | "spam"
  | "fake_event"
  | "other";

export type ReportStatus = "pending" | "reviewed";

export interface UserReport {
  report_id: string;
  reporter_id: string | null;
  reported_user_id: string;
  report_slug: string;
  category: ReportCategory;
  description: string | null;
  status: ReportStatus;
  admin_notes: string | null;
  created_at: string;
  content_type: string;
  content_id: string;
  jev_flagged: boolean | null;
  jev_confidence: number | null;
}

export interface ReportsPage {
  data: UserReport[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface ReportWithUsers extends UserReport {
  reporter_username: string | null;
  reporter_email: string | null;
  reported_username: string;
  reported_email: string;
}

export interface DetailedReportsPage {
  data: ReportWithUsers[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface UpdateReportPayload {
  status: ReportStatus;
  admin_notes?: string;
}

export type EnforcementAction = "issue_warning" | "strike_user" | "ban_user";

export interface Enforcement {
  enforcement_id: string;
  user_id: string;
  admin_user_id: string;
  report_id: string | null;
  action: EnforcementAction;
  reason: string;
  expires_at: string | null;
  lifted_at: string | null;
  lifted_by: string | null;
  created_at: string;
}

export interface EnforcementsPage {
  data: Enforcement[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface CreateEnforcementPayload {
  user_id: string;
  action: EnforcementAction;
  reason: string;
  report_id?: string;
  expires_at?: string;
}

export interface UserEnforcementSummary {
  user_id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  active_card_id: string | null;
  date_of_birth: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  is_deleted: boolean;
  credits: number;
  xp: number;
  check_in_count: number;
  enforcement_count: number;
  active_enforcement_count: number;
  warning_count: number;
  strike_count: number;
  ban_count: number;
  actions: EnforcementAction[];
  last_action: EnforcementAction | null;
  last_enforcement_at: string | null;
  // null = not locked out; 9999-12-31T23:59:59Z = permanent
  suspended_until: string | null;
}

export interface MyEnforcementStatus {
  action: EnforcementAction;
  reason: string;
  expires_at: string | null;
  created_at: string;
}

export interface UserEnforcementSummariesPage {
  data: UserEnforcementSummary[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface ReportQueueSummary {
  pending_count: number;
  pending_over_24h_count: number;
  oldest_pending_at: string | null;
}

class SafetyProvider extends BaseProvider {
  listReports(params?: {
    status?: ReportStatus;
    page?: number;
    limit?: number;
  }): Promise<ReportsPage> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    const query = qs.toString() ? `?${qs}` : "";
    return this.request<ReportsPage>(`/v1/admin/reports${query}`);
  }

  listReportsDetailed(params?: {
    status?: ReportStatus;
    page?: number;
    limit?: number;
  }): Promise<DetailedReportsPage> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    const query = qs.toString() ? `?${qs}` : "";
    return this.request<DetailedReportsPage>(`/v1/admin/reports/detailed${query}`);
  }

  updateReport(reportId: string, payload: UpdateReportPayload): Promise<UserReport> {
    return this.request<UserReport>(`/v1/admin/reports/${encodeURIComponent(reportId)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  createEnforcement(payload: CreateEnforcementPayload): Promise<Enforcement> {
    return this.request<Enforcement>("/v1/admin/enforcements", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  liftEnforcement(enforcementId: string, reason: string): Promise<Enforcement> {
    return this.request<Enforcement>(
      `/v1/admin/enforcements/${encodeURIComponent(enforcementId)}`,
      { method: "DELETE", body: JSON.stringify({ reason }) }
    );
  }

  listEnforcements(params?: {
    active?: boolean;
    page?: number;
    limit?: number;
  }): Promise<EnforcementsPage> {
    const qs = new URLSearchParams();
    if (params?.active !== undefined) qs.set("active", String(params.active));
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    const query = qs.toString() ? `?${qs}` : "";
    return this.request<EnforcementsPage>(`/v1/admin/enforcements${query}`);
  }

  listUserEnforcementSummaries(params?: {
    enforced_only?: boolean;
    page?: number;
    limit?: number;
  }): Promise<UserEnforcementSummariesPage> {
    const qs = new URLSearchParams();
    if (params?.enforced_only !== undefined)
      qs.set("enforced_only", String(params.enforced_only));
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    const query = qs.toString() ? `?${qs}` : "";
    return this.request<UserEnforcementSummariesPage>(
      `/v1/admin/users/enforcements${query}`
    );
  }

  listUserEnforcements(userId: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<EnforcementsPage> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    const query = qs.toString() ? `?${qs}` : "";
    return this.request<EnforcementsPage>(
      `/v1/admin/users/${encodeURIComponent(userId)}/enforcements${query}`
    );
  }

  getReportQueueSummary(): Promise<ReportQueueSummary> {
    return this.request<ReportQueueSummary>("/v1/admin/reports/summary");
  }

  // 200 = currently banned, 404 = not banned (the normal case).
  getMyEnforcement(): Promise<MyEnforcementStatus | null> {
    return this.requestOrNullOn404<MyEnforcementStatus>(
      "/v1/users/me/enforcement",
      undefined,
      true
    );
  }
}

export const safetyProvider = new SafetyProvider();
